import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { isEqual } from 'lodash';
import { applyClasses, applyProps, useKeyPress, constants } from '../shared/js';
import { useAfterFirstRender } from '../../hooks/useAfterFirstRender';
import styles from './switch.module.scss';

const { LEFT_KEY, RIGHT_KEY, UP_KEY, DOWN_KEY } = constants;

const Switch = ({
  options,
  name,
  validation,
  onChange,
  disabled,
  value,
  label,
  ...props
}) => {
  const [active, setActive] = useState(false);
  const [currentlyFocused, setCurrentlyFocused] = useState();
  const formMethods = useFormContext();
  const register = formMethods?.register ? formMethods?.register : () => {};
  const formProps = register(name, { ...validation });
  const leftPressed = useKeyPress(LEFT_KEY);
  const upPressed = useKeyPress(UP_KEY);
  const rightPressed = useKeyPress(RIGHT_KEY);
  const downPressed = useKeyPress(DOWN_KEY);

  useEffect(() => {
    if (!active || (!upPressed && !leftPressed)) return;
    if (typeof currentlyFocused === 'undefined') setCurrentlyFocused(0);
    else if (currentlyFocused === 0) setCurrentlyFocused(options.length - 1);
    else setCurrentlyFocused(currentlyFocused - 1);
  }, [upPressed, leftPressed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active || (!downPressed && !rightPressed)) return;
    if (typeof currentlyFocused === 'undefined') setCurrentlyFocused(0);
    else if (currentlyFocused >= options.length - 1) setCurrentlyFocused(0);
    else setCurrentlyFocused(currentlyFocused + 1);
  }, [downPressed, rightPressed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof currentlyFocused === 'undefined') return;
    const input = document.getElementById(
      `${name}${options[currentlyFocused]?.id}`
    );
    const label = document.getElementById(
      `${name}${options[currentlyFocused]?.id}label`
    );
    label?.focus();
    input?.click();
  }, [currentlyFocused]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof value === 'undefined') return;
    const option = options.findIndex((o) => o.value === value);
    const input = document.getElementById(`${name}${options[option]?.id}`);
    input?.click();
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useAfterFirstRender(() => {
    formProps?.setValue?.(name, `${value}`);
    onChange(value);
  }, [value]);

  const handleChange = (e, value) => {
    formProps?.onChange?.(e);
    onChange(value);
  };

  return (
    <>
      {label && <label>{label}</label>}
      <div
        // eslint-disable-next-line react/prop-types
        className={cn(styles.switch, props.className)}
        tabIndex={0}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
      >
        {options.map((option) => (
          <div key={option.value} className={styles.option}>
            <input
              id={`${name}${option.id}`}
              className={cn(
                styles.input,
                // eslint-disable-next-line react/prop-types
                props.optionClassName,
                { [styles.disabled]: disabled },
                applyClasses(props, styles)
              )}
              name={name}
              type="radio"
              value={option.value}
              defaultChecked={value && isEqual(value, option.value)}
              disabled={disabled}
              {...formProps}
              {...applyProps(props)}
              onChange={(e) => handleChange(e, option.value)}
              tabIndex={-1}
            />
            <label
              id={`${name}${option.id}label`}
              htmlFor={`${name}${option.id}`}
              className={styles.label}
              aria-label={option.label}
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>
    </>
  );
};

Switch.propTypes = {
  /** The name of the inputs rendered */
  name: PropTypes.string,
  /** A callback called when an option is selected */
  onChange: PropTypes.func,
  /** Whether or not the options are disabled */
  disabled: PropTypes.bool,
  /** A validation callback function */
  validation: PropTypes.object,
  /** Programatically select the value outside of a Form component */
  value: PropTypes.any,
  /** A label for the input */
  label: PropTypes.any,
  /** The options */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /** The ID of the option, used for the label */
      id: PropTypes.string.isRequired,
      /** The label text of the option */
      label: PropTypes.string.isRequired,
      /** The value associated with the option */
      value: PropTypes.any.isRequired,
    })
  ),
};

Switch.defaultProps = {
  onChange: () => {},
  name: undefined,
  options: [],
  disabled: false,
  validation: {},
  value: undefined,
  label: undefined,
};

export default Switch;
